/**
 * ConflictDialog - Version conflict notification dialog
 *
 * Displays when a version conflict is detected (HTTP 412).
 * Provides options to refresh and load the latest version or dismiss.
 *
 * @module features/storage/components/ConflictDialog
 * @see SPEC-006-background-cloud-save-queue
 * @see Requirements 4.3, 4.6
 */

import { memo, useCallback, useEffect, useRef } from 'react';
import { useTypedTranslation } from '../../../i18n';

/**
 * Props for ConflictDialog component
 */
export interface ConflictDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Document ID that has the conflict */
  docId: string;
  /** Callback when user chooses to refresh */
  onRefresh: () => void;
  /** Callback when user dismisses the dialog */
  onDismiss: () => void;
}

/**
 * ConflictDialog component
 *
 * Modal dialog that appears when a version conflict is detected.
 * User can choose to refresh the page to load the latest version,
 * or dismiss to handle the conflict later.
 *
 * @see Requirements 4.3 - Prompt user to refresh on conflict
 * @see Requirements 4.6 - Display clear conflict notification with action options
 *
 * @example
 * ```tsx
 * <ConflictDialog
 *   isOpen={hasConflict}
 *   docId="doc-123"
 *   onRefresh={() => window.location.reload()}
 *   onDismiss={() => setHasConflict(false)}
 * />
 * ```
 */
export const ConflictDialog = memo(function ConflictDialog({
  isOpen,
  docId,
  onRefresh,
  onDismiss,
}: ConflictDialogProps) {
  const { t } = useTypedTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const refreshButtonRef = useRef<HTMLButtonElement>(null);

  // Focus management - focus the refresh button when dialog opens
  useEffect(() => {
    if (isOpen && refreshButtonRef.current) {
      refreshButtonRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key to dismiss
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onDismiss();
      }
    },
    [onDismiss]
  );

  // Trap focus within dialog
  const handleTabKey = useCallback((event: React.KeyboardEvent) => {
    if (event.key !== 'Tab' || !dialogRef.current) return;

    const focusableElements = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement?.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement?.focus();
    }
  }, []);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      role="presentation"
      onKeyDown={(e) => {
        handleKeyDown(e);
        handleTabKey(e);
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity duration-200"
        onClick={onDismiss}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="conflict-dialog-title"
        aria-describedby="conflict-dialog-description"
        className="relative z-10 w-full max-w-md mx-4 bg-white rounded-lg shadow-xl transform transition-all duration-200"
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4">
          <div className="flex items-center gap-3">
            {/* Warning Icon */}
            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center">
              <span className="text-yellow-600 text-xl" aria-hidden="true">
                {t('conflictDialog.warningIcon')}
              </span>
            </div>
            <h2 id="conflict-dialog-title" className="text-lg font-semibold text-gray-900">
              {t('conflictDialog.title')}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 pb-4">
          <p id="conflict-dialog-description" className="text-sm text-gray-600 leading-relaxed">
            {t('conflictDialog.message')}
          </p>
          {/* Show document ID for debugging purposes */}
          <p className="mt-2 text-xs text-gray-400 font-mono">
            {t('conflictDialog.documentLabel')} {docId}
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
          <button
            type="button"
            onClick={onDismiss}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
          >
            {t('conflictDialog.dismiss')}
          </button>
          <button
            ref={refreshButtonRef}
            type="button"
            onClick={onRefresh}
            className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-150"
          >
            {t('conflictDialog.refresh')}
          </button>
        </div>
      </div>
    </div>
  );
});

export default ConflictDialog;
